import { createMuiTheme } from '@material-ui/core';

import { tokens } from './tokens';

export const theme = createMuiTheme({
  shape: {
    borderRadius: tokens.radius.sm, // 8px (was 4px MUI default)
  },

  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    body1: {
      fontSize: '0.8125rem',
    },
    body2: {
      fontSize: '0.75rem',
    },
    h1: {
      fontSize: '3.57em',
    },
    h2: {
      fontSize: '3.075em',
    },
    h3: {
      fontSize: '1.929em',
    },
    caption: {
      color: '#757575',
    },
  },

  palette: {
    background: {
      default: tokens.colors.bg,
    },
    primary: {
      main: tokens.colors.primary,
      dark: '#0e45dd',
      light: '#577ee9',
    },
    secondary: {
      main: tokens.colors.error,
    },
    error: {
      main: tokens.colors.error,
    },
    success: {
      main: tokens.colors.success,
    },
    warning: {
      main: tokens.colors.warning,
    },
    text: {
      secondary: '#191945',
    },
  },

  overrides: {
    // NOTE: No global MuiPaper overrides -- card-specific styling is in
    // OfferList/components/styles.ts to avoid side-effects on Stepper,
    // form containers, and other Paper-based MUI internals.

    MuiInputBase: {
      input: {
        fontSize: 16, // Prevents iOS Safari zoom on focus (requires >= 16px)
      },
    },

    MuiButton: {
      label: {
        fontWeight: 700,
        padding: '5px 10px',
        '& > div': {
          padding: 7,
        },
      },
      root: {
        textTransform: 'initial',
        borderRadius: tokens.radius.sm,
        transition: 'all 0.15s ease',
      },
      contained: {
        boxShadow: tokens.shadows.sm,
        '&:hover': {
          boxShadow: tokens.shadows.hover,
        },
      },
      outlined: {
        borderColor: tokens.colors.borderSubtle,
        '&:hover': {
          borderColor: tokens.colors.primary,
          backgroundColor: tokens.colors.primaryLight,
        },
      },
    },

    MuiOutlinedInput: {
      input: {
        padding: 10,
        fontSize: 16, // Prevents iOS Safari zoom on focus
      },
      notchedOutline: {
        borderWidth: '1px !important',
      },
      root: {
        backgroundColor: tokens.colors.surface,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover $notchedOutline': {
          borderColor: tokens.colors.primary,
        },
        '&$focused $notchedOutline': {
          borderColor: tokens.colors.primary,
          boxShadow: `0 0 0 3px ${tokens.colors.primaryLight}`,
        },
        '&$disabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        },
        '&$disabled $notchedOutline': {
          borderColor: 'rgba(0, 0, 0, 0.1)',
        },
      },
      adornedEnd: {
        '& svg': {
          opacity: 0.5,
        },
      },
    },

    MuiStepLabel: {
      label: {
        fontWeight: 600,
      },
    },

    MuiFormHelperText: {
      contained: {
        marginLeft: 0,
      },
    },

    MuiChip: {
      colorPrimary: {
        backgroundColor: tokens.colors.surface,
        color: tokens.colors.textPrimary,
        fontSize: 13,
        height: 30,
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.colors.borderSubtle}`,
        transition: 'all 0.15s ease',
      },
      label: {
        paddingLeft: 12,
        paddingRight: 12,
      },
    },

    MuiSlider: {
      rail: {
        backgroundColor: tokens.colors.borderSubtle,
      },
      track: {
        height: 3,
        color: tokens.colors.primary,
      },
      thumb: {
        border: `3px solid ${tokens.colors.surface}`,
        color: tokens.colors.primary,
        height: 18,
        width: 18,
        boxShadow: tokens.shadows.sm,
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
          boxShadow: tokens.shadows.hover,
        },
        '&$disabled': {
          height: 18,
          width: 18,
          marginTop: -6,
        },
      },
    },

    MuiTooltip: {
      tooltip: {
        color: tokens.colors.textSecondary,
        backgroundColor: tokens.colors.surface,
        border: `1px solid ${tokens.colors.borderSubtle}`,
        borderRadius: tokens.radius.sm,
        boxShadow: tokens.shadows.sm,
        fontSize: 12,
      },
      arrow: {
        color: tokens.colors.borderSubtle,
      },
    },

    MuiFormControlLabel: {
      label: {
        textAlign: 'left',
      },
    },

    MuiCheckbox: {
      root: {
        alignSelf: 'flex-start',
      },
    },

    MuiRadio: {
      root: {
        color: 'rgba(0, 0, 0, 0.1)',
      },
    },

    MuiFormLabel: {
      root: {
        marginBottom: 12,
      },
    },
  },
});
